'use client';

import { cn } from '@core/utils';

import SvgChevronleft from '../../../__generated__/icon/SvgChevronleft';
import SvgChevronright from '../../../__generated__/icon/SvgChevronright';
import SvgChevronrightDouble from '../../../__generated__/icon/SvgChevronrightDouble';
import SvgChevronrleftDouble from '../../../__generated__/icon/SvgChevronrleftDouble';

export interface PaginationProps {
  totalCount?: number;
  currentPage?: number;
  size?: number;
  onMovePage?: (pageNum: number) => void;
  perButtonCount?: number;
  responsible?: boolean;
  alwaysShowEdgeButtons?: boolean;
  className?: string;
}

export const Pagination = ({
  size = 20,
  onMovePage = () => {},
  totalCount = 0,
  currentPage = 1,
  perButtonCount = 5,
  responsible = true,
  alwaysShowEdgeButtons = true,
  className,
}: PaginationProps) => {
  if (!totalCount) return null;

  const totalPages = Math.ceil(totalCount / size);
  const safeCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
  const groupIndex = Math.ceil(safeCurrentPage / perButtonCount);
  const lastPage = Math.min(perButtonCount * groupIndex, totalPages);
  const startPage = Math.max(1, lastPage - perButtonCount + 1);

  const pages: number[] = [];
  for (let i = startPage; i <= lastPage; i++) pages.push(i);

  if (!pages.length) return null;

  const isFirstPage = safeCurrentPage === 1;
  const isLastPage = safeCurrentPage === totalPages;

  const baseButtonClassName =
    'flex size-32 items-center justify-center rounded-xxsmall border-[0.75px] border-Gray-300 bg-Gray-white';

  const navButtonClassName = (className?: string) =>
    cn(
      baseButtonClassName,
      'text-Gray-600 transition-colors hover:border-Gray-400 hover:text-Gray-700 active:border-Gray-500 active:text-Gray-800 disabled:cursor-default disabled:border-Gray-300 disabled:text-Gray-300',
      className,
    );

  const pageButtonClassName = (isCurrentPage: boolean) =>
    cn(
      baseButtonClassName,
      isCurrentPage
        ? 'font-600 text-Gray-800 typo-body4'
        : 'font-400 text-Gray-600 transition-colors typo-body4 hover:border-Gray-400 hover:text-Gray-700 active:border-Gray-500 active:text-Gray-800',
    );

  const edgeButtonClassName = cn({
    'mobile:hidden': responsible,
  });

  return (
    <nav aria-label="페이지 이동">
      <ul
        className={cn(
          'my-40 flex items-center justify-center gap-12',
          className,
        )}
      >
        {alwaysShowEdgeButtons || !isFirstPage ? (
          <li>
            <button
              aria-label="처음 페이지"
              type="button"
              className={navButtonClassName(edgeButtonClassName)}
              disabled={isFirstPage}
              onClick={(e) => {
                e.preventDefault();
                onMovePage(1);
              }}
            >
              <SvgChevronrleftDouble className="size-16" />
            </button>
          </li>
        ) : null}
        <li>
          <button
            aria-label="이전 페이지"
            type="button"
            className={navButtonClassName()}
            disabled={isFirstPage}
            onClick={(e) => {
              e.preventDefault();
              onMovePage(safeCurrentPage - 1);
            }}
          >
            <SvgChevronleft className="size-16" />
          </button>
        </li>
        {pages.map((pageNum) => {
          const isCurrentPage = pageNum === safeCurrentPage;

          return (
            <li key={pageNum}>
              <button
                aria-current={isCurrentPage ? 'page' : undefined}
                type="button"
                className={pageButtonClassName(isCurrentPage)}
                onClick={(e) => {
                  e.preventDefault();

                  if (isCurrentPage) return;

                  onMovePage(pageNum);
                }}
              >
                {pageNum}
              </button>
            </li>
          );
        })}
        <li>
          <button
            aria-label="다음 페이지"
            type="button"
            className={navButtonClassName()}
            disabled={isLastPage}
            onClick={(e) => {
              e.preventDefault();
              onMovePage(safeCurrentPage + 1);
            }}
          >
            <SvgChevronright className="size-16" />
          </button>
        </li>
        {alwaysShowEdgeButtons || !isLastPage ? (
          <li>
            <button
              aria-label="마지막 페이지"
              type="button"
              className={navButtonClassName(edgeButtonClassName)}
              disabled={isLastPage}
              onClick={(e) => {
                e.preventDefault();
                onMovePage(totalPages);
              }}
            >
              <SvgChevronrightDouble className="size-16" />
            </button>
          </li>
        ) : null}
      </ul>
    </nav>
  );
};
